import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Gavel, Loader2, MessageSquare, Search, Upload, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileChips, Messages, StatePill, Timeline, daysLeft, statusOf } from "@/components/submissions/shared";
import { fmtDateTime } from "@/lib/time";
import { errText } from "@/lib/api";
import { PortalFrame } from "@/components/site/ContactPortal";

const APPEALING = ["appeal_submitted", "appeal_review"];
const handledLabel = (s) => {
  if (APPEALING.includes(s.status)) return "Your appeal has been assigned to";
  if (s.status === "appeal_declined") return "Appeal handled by";
  return s.closed ? "Handled by" : "Your submission has been assigned to";
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full border border-black/20 bg-white px-4 py-3.5 text-base outline-none transition-shadow focus:border-black focus:ring-4 focus:ring-[#E62314]/15";
const ENDED = ["rejected", "declined", "appeal_declined"];

const Bar = ({ steps, at, title }) => (
  <div className="mt-8">
    {title && <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400" data-testid="status-second-look">{title}</p>}
    <div className="grid grid-cols-3 gap-2">
      {steps.map((label, i) => (
        <div key={i}>
          <div className="h-1.5 overflow-hidden bg-white/15"><motion.div initial={{ width: 0 }} animate={{ width: i <= at ? "100%" : "0%" }} transition={{ duration: 0.7, delay: i * 0.2 }} className="h-full bg-[#E62314]" /></div>
          <p className={`mt-2 text-xs font-semibold ${i <= at ? "text-white" : "text-zinc-400"}`}>{label}</p>
        </div>
      ))}
    </div>
  </div>
);

const segStatus = (evs, start) => [...evs].reverse().find((e) => e.status && !["closed", "reopened", ...APPEALING].includes(e.status))?.status || start;
const stepOf = (st, start) => (ENDED.includes(st) ? 2 : st === start ? 0 : 1);

const Progress = ({ status, history }) => {
  const events = Array.isArray(history) ? history : [];
  const reopenAt = events.findIndex((e) => e.status === "reopened");
  const start = reopenAt >= 0 ? "reopened" : "pending";
  const current = APPEALING.includes(status) ? segStatus(reopenAt >= 0 ? events.slice(reopenAt) : events, start) : status;
  const label = (st) => statusOf(st).label;
  const outcome = (st) => (ENDED.includes(st) ? label(st) : "Outcome");
  const first = reopenAt >= 0 ? segStatus(events.slice(0, reopenAt), "pending") : null;
  return (
    <div data-testid="status-progress">
      {first && <Bar steps={[label("pending"), label("in_progress"), outcome(first)]} at={stepOf(first, "pending")} />}
      <Bar title={first ? "Second look" : ""} steps={[label(start), label("in_progress"), outcome(current)]} at={stepOf(current, start)} />
    </div>
  );
};

const FollowUp = ({ open, onOpenChange, creds, onDone }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setMsg(""); return undefined; }, [open]);
  const send = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/submissions/track/follow-up`, { ...creds, message: msg.trim() }); onDone(r.data); onOpenChange(false); toast.success("Chase-up sent. We'll reply as soon as we can"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-[#FBF9F5] sm:max-w-lg" data-testid="follow-up-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Chase up your submission</DialogTitle><DialogDescription>Haven't heard from us in a while? Send us a message and we'll get back to you. This doesn't change your place in the queue. You can chase up again once we've replied.</DialogDescription></DialogHeader>
        <textarea rows={5} maxLength={3000} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="e.g. Just checking in on my submission, it's been three weeks" className={field} data-testid="follow-up-message" />
        <button onClick={send} disabled={busy || !msg.trim()} className="flex w-full items-center justify-center gap-2 bg-[#0A0A0A] py-3.5 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50" data-testid="follow-up-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send chase-up</button>
      </DialogContent>
    </Dialog>
  );
};

const Appeal = ({ open, onOpenChange, creds, appeal, onDone }) => {
  const qs = appeal?.questions || [];
  const [answers, setAnswers] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (open) { setAnswers(qs.map(() => "")); setFiles([]); } return undefined; }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("ref", creds.ref); fd.append("email", creds.email); fd.append("answers", JSON.stringify(answers));
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/submissions/track/appeal`, fd); onDone(r.data); onOpenChange(false); toast.success("Appeal submitted");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const ready = answers.every((a) => a.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-none bg-[#FBF9F5] sm:max-w-lg" data-testid="appeal-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Appeal to reopen</DialogTitle><DialogDescription>Ask us to take a second look at your submission and reopen it. {daysLeft(appeal?.deadline)} days left to appeal.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {qs.map((q, i) => (
            <label key={i} className="block"><span className="mb-1.5 block text-sm font-semibold">{q}</span>
              <textarea rows={3} maxLength={3000} value={answers[i] || ""} onChange={(e) => setAnswers((a) => a.map((x, j) => (j === i ? e.target.value : x)))} className={field} data-testid={`appeal-answer-${i}`} /></label>
          ))}
          {!qs.length && <p className="text-sm text-[#57534E]">Press submit to ask us to look at your submission again.</p>}
          {appeal?.allow_upload && (
            <div>
              <button type="button" onClick={() => ref.current?.click()} className="flex items-center gap-2 border border-dashed border-black/30 bg-white px-4 py-3 text-sm font-semibold hover:border-black" data-testid="appeal-upload"><Upload className="h-4 w-4" />Add supporting files</button>
              <input ref={ref} type="file" multiple hidden onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 5)); e.target.value = ""; }} data-testid="appeal-file-input" />
              <ul className="mt-2 space-y-1">{files.map((f, i) => <li key={i} className="flex items-center justify-between gap-2 text-xs"><span className="truncate">{f.name}</span><button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} aria-label="Remove file"><X className="h-3.5 w-3.5" /></button></li>)}</ul>
            </div>
          )}
          <button onClick={send} disabled={busy || !ready} className="flex w-full items-center justify-center gap-2 bg-[#E62314] py-3.5 text-sm font-bold text-white hover:bg-[#0A0A0A] disabled:opacity-50" data-testid="appeal-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Submit appeal</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Answer = ({ question, onOpenChange, creds, onDone }) => {
  const [msg, setMsg] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (question) { setMsg(""); setFiles([]); } return undefined; }, [question]);
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("ref", creds.ref); fd.append("email", creds.email); fd.append("question_id", question.id); fd.append("message", msg.trim());
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/submissions/track/answer`, fd); onDone(r.data); onOpenChange(false); toast.success("Reply sent");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!question} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-none bg-[#FBF9F5] sm:max-w-lg" data-testid="answer-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Reply to our message</DialogTitle><DialogDescription className="whitespace-pre-wrap">{question?.text}</DialogDescription></DialogHeader>
        <textarea rows={5} maxLength={5000} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Your reply" className={field} data-testid="answer-message" />
        <div>
          <button type="button" onClick={() => ref.current?.click()} className="flex items-center gap-2 border border-dashed border-black/30 bg-white px-4 py-2.5 text-sm font-semibold hover:border-black" data-testid="answer-upload"><Upload className="h-4 w-4" />Attach files (optional)</button>
          <input ref={ref} type="file" multiple hidden onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 5)); e.target.value = ""; }} data-testid="answer-file-input" />
          <ul className="mt-2 space-y-1">{files.map((f, i) => <li key={i} className="flex items-center justify-between gap-2 text-xs"><span className="truncate">{f.name}</span><button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} aria-label="Remove file"><X className="h-3.5 w-3.5" /></button></li>)}</ul>
        </div>
        <button onClick={send} disabled={busy || !msg.trim()} className="flex w-full items-center justify-center gap-2 bg-[#0A0A0A] py-3.5 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50" data-testid="answer-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send reply</button>
      </DialogContent>
    </Dialog>
  );
};

const Result = ({ s, creds, onChange }) => {
  const [dialog, setDialog] = useState("");
  const [question, setQuestion] = useState(null);
  const waiting = (s.history || []).filter((e) => ["question", "reply"].includes(e.kind) && !e.answered && e.allow_reply !== false);
  const closeNote = s.closed ? [...(s.history || [])].reverse().find((e) => e.kind === "status" && e.status === "closed")?.text : "";
  const fileHref = (f) => `${API}/submissions/track/files/${f.id}?ref=${encodeURIComponent(creds.ref)}&email=${encodeURIComponent(creds.email)}`;
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="mt-12" data-testid="status-result">
      <div className="relative overflow-hidden border border-white/10 bg-[#111315] p-6 text-white sm:p-10">
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-sm tracking-[0.3em] text-zinc-400" data-testid="status-ref">{s.ref}</p>
            <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="status-label">{s.status_label}</h2>
            <p className="mt-2 break-words text-sm text-zinc-300">{s.category}{s.topic ? ` · ${s.topic}` : ""} · Sent {fmtDateTime(s.created_at)}</p>
            {s.handled_by ? (
              <p className="mt-4 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full bg-white/10 px-4 py-2 text-sm" data-testid="status-handled-by">
                <UserRound className="h-4 w-4 shrink-0 text-[#E62314]" /><span className="text-zinc-300">{handledLabel(s)}</span>
                <span className="font-bold text-white">{s.handled_by.name}</span>{s.reassign_pending && <span className="text-zinc-300" data-testid="status-reassign-pending">· reassignment pending</span>}
              </p>
            ) : !s.closed || APPEALING.includes(s.status) ? (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-zinc-300" data-testid="status-unassigned"><UserRound className="h-4 w-4 shrink-0" />{APPEALING.includes(s.status) ? "Your appeal isn't assigned yet" : "Not assigned yet"} · {s.reassign_pending ? "assignment pending" : "waiting for a member of the Cresway Media team to pick it up"}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatePill closed={s.closed} testId="status-state" />
          </div>
        </div>
        {closeNote && (
          <div className="relative mt-6 border-l-2 border-[#E62314] pl-4" data-testid="status-close-note">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Note from our team</p>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-zinc-100">{closeNote}</p>
          </div>
        )}
        <Progress status={s.status} history={s.history} />
        <div className="relative mt-8 flex flex-wrap gap-3">
          {s.can_follow_up && <button onClick={() => setDialog("follow")} className="flex items-center gap-2 bg-white px-5 py-2.5 text-sm font-bold text-black hover:bg-[#FF4A36]" data-testid="status-follow-up"><MessageSquare className="h-4 w-4" />Chase up my submission</button>}
          {s.appeal?.open && <button onClick={() => setDialog("appeal")} className="flex items-center gap-2 bg-[#FF4A36] px-5 py-2.5 text-sm font-bold text-black hover:bg-white" data-testid="status-appeal"><Gavel className="h-4 w-4" />Appeal to reopen · {daysLeft(s.appeal.deadline)} days left</button>}
        </div>
      </div>
      {waiting.length > 0 && (
        <div className="border-x border-white/10 bg-[#FF4A36]/10 p-5 text-white sm:px-10" data-testid="open-questions">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF8A7B]">We have {waiting.length === 1 ? "a message" : `${waiting.length} messages`} for you</p>
          <ul className="mt-3 space-y-3">{waiting.map((q) => (
            <li key={q.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <p className="min-w-0 whitespace-pre-wrap break-words text-sm font-semibold">{q.text}</p>
              <button onClick={() => setQuestion(q)} className="shrink-0 self-start bg-[#FF4A36] px-5 py-2 text-xs font-bold text-black hover:bg-white sm:self-auto" data-testid={`open-question-${q.id}-reply`}>Reply</button>
            </li>
          ))}</ul>
        </div>
      )}
      <div className="grid gap-10 border border-t-0 border-white/10 bg-white/[0.035] p-6 text-white sm:p-10 lg:grid-cols-[1fr_1.2fr]">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/45">Your message</p>
          <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-white/80">{s.message}</p>
          {s.files?.length > 0 && <FileChips files={s.files} fileHref={fileHref} />}
        </div>
        <div className="min-w-0">
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-white/45">Status history</p>
          <div className="border border-white/10 bg-[#191B1D] p-5"><Timeline history={s.history} fileHref={fileHref} dark /></div>
        </div>
        <div className="min-w-0 lg:col-span-2">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white/45">Message history</p>
          <div className="border border-white/10 bg-[#191B1D] p-5"><Messages history={s.history} fileHref={fileHref} dark /></div>
        </div>
      </div>
      <FollowUp open={dialog === "follow"} onOpenChange={(o) => setDialog(o ? "follow" : "")} creds={creds} onDone={onChange} />
      <Appeal open={dialog === "appeal"} onOpenChange={(o) => setDialog(o ? "appeal" : "")} creds={creds} appeal={s.appeal} onDone={onChange} />
      <Answer question={question} onOpenChange={(o) => { if (!o) setQuestion(null); }} creds={creds} onDone={onChange} />
    </motion.div>
  );
};

export default function CheckStatus() {
  const [params] = useSearchParams();
  const [creds, setCreds] = useState({ ref: params.get("ref") || "", email: "" });
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const look = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const r = await axios.post(`${API}/submissions/track`, { ref: creds.ref.trim(), email: creds.email.trim() }); setFound({ s: r.data, creds: { ref: creds.ref.trim(), email: creds.email.trim() } }); }
    catch (err) { setFound(null); setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <PortalFrame centered heading="Check an inquiry." description="Enter the reference number and email address you used when you contacted us." utilityHref="/contact" utilityLabel="Contact us" step={found ? "Inquiry status" : "Status check"}>
      <div className="mx-auto w-full max-w-3xl" data-testid="check-status-page">
        <h2 className="font-display text-2xl font-extrabold">Find your inquiry.</h2>
        <form onSubmit={look} className="mt-6 grid gap-4 md:grid-cols-[1fr_1.3fr_auto]" data-testid="status-form">
          <label className="block text-sm font-bold" htmlFor="status-ref">Reference number<input id="status-ref" value={creds.ref} onChange={(e) => setCreds({ ...creds, ref: e.target.value.toUpperCase() })} placeholder="CM-XXXXXX" required className="mt-2 w-full border border-white/20 bg-white/[0.04] px-4 py-3.5 font-mono text-base tracking-widest text-white outline-none focus:border-[#FF4A36]" data-testid="status-ref-input" /></label>
          <label className="block text-sm font-bold" htmlFor="status-email">Email address<input id="status-email" type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} placeholder="name@example.com" required className="mt-2 w-full border border-white/20 bg-white/[0.04] px-4 py-3.5 text-base text-white outline-none focus:border-[#FF4A36]" data-testid="status-email-input" /></label>
          <button type="submit" disabled={busy} className="mt-auto flex min-h-12 items-center justify-center gap-2 bg-[#FF4A36] px-5 text-sm font-bold text-black transition-colors hover:bg-white disabled:opacity-60" data-testid="status-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Check status</button>
        </form>
        {error && <p className="mt-4 border-l-2 border-[#FF4A36] pl-3 text-sm font-semibold text-[#FF8A7B]" data-testid="status-error">{error}</p>}
        {found && <Result s={found.s} creds={found.creds} onChange={(s) => setFound((f) => ({ ...f, s }))} />}
      </div>
    </PortalFrame>
  );
}
